// The installable app's two small prompts (styles in pwa.css):
// - InstallHint: signed-in phones only, under the top bar. iOS Safari: "tap Share, then Add to Home Screen" (it has no
//   install prompt); Chrome/Android: an Install button. Dismissed once, it stays dismissed (localStorage).
// - UpdatePrompt: a newer version has installed; tapping reloads into it. It also applies by itself on the next move to
//   another screen when no Director call is open (a screen change loses nothing), so few people ever see the toast.
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLocation } from "../router";
import { get } from "../store";
import { Icon } from "../components/bits";
import { installMode, installPrompt, isPhone, isStandalone, promptInstall, rememberDismissal, subscribeInstall, wasDismissed, wasInstalled } from "./install";
import { applyUpdate, subscribeUpdate, updateWaiting } from "./register";

/** The iOS share glyph (a box with an arrow out of the top), as Safari draws it. */
const ShareGlyph = () => (
  <svg className="install-share" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
    <path d="M12 3v12M8 7l4-4 4 4M8 11H6.5A1.5 1.5 0 0 0 5 12.5v7A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-7a1.5 1.5 0 0 0-1.5-1.5H16" />
  </svg>
);

export function InstallHint() {
  const prompt = useSyncExternalStore(subscribeInstall, installPrompt, () => null);
  const installed = useSyncExternalStore(subscribeInstall, wasInstalled, () => false);
  const [dismissed, setDismissed] = useState(wasDismissed);
  // measured once on mount: these don't change while the page is open
  const [env] = useState(() => ({ userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent, standalone: isStandalone(), phone: isPhone() }));
  const mode = installed ? null : installMode({ ...env, hasPrompt: !!prompt, dismissed });
  if (!mode) return null;

  const dismiss = () => {
    rememberDismissal();
    setDismissed(true);
  };
  return (
    <aside className="install-hint" aria-label="Install CAO Studio">
      <img className="install-icon" src="/icon-192.png" width={36} height={36} alt="" />
      {mode === "ios" ? (
        <p className="install-text">
          <b>Add CAO Studio to your Home Screen.</b> Tap <ShareGlyph />
          <span className="sr-only">Share</span>, then <b>Add to Home Screen</b>.
        </p>
      ) : (
        <>
          <p className="install-text">
            <b>Install CAO Studio</b> on your home screen.
          </p>
          <button
            type="button"
            className="btn btn-primary install-btn"
            onClick={async () => {
              if (await promptInstall()) dismiss();
            }}
          >
            Install
          </button>
        </>
      )}
      <button type="button" className="icon-btn install-close" aria-label="Don't show this again" onClick={dismiss}>
        <Icon name="close" size={18} />
      </button>
    </aside>
  );
}

export function UpdatePrompt() {
  const waiting = useSyncExternalStore(subscribeUpdate, updateWaiting, () => false);
  const [busy, setBusy] = useState(false);
  const { path } = useLocation();
  const lastPath = useRef(path);
  useEffect(() => {
    if (!waiting) setBusy(false);
  }, [waiting]);
  // moving to another screen with an update waiting: reload into it then (never during a call)
  useEffect(() => {
    if (path === lastPath.current) return;
    lastPath.current = path;
    if (waiting && !busy && !get().directorCall) {
      setBusy(true);
      applyUpdate();
    }
  }, [path, waiting, busy]);
  if (!waiting) return null;
  return (
    <button
      type="button"
      className="toast update-toast"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        applyUpdate();
      }}
    >
      <Icon name="sparkle" size={16} />
      <span>
        <b>A new version is ready.</b> {busy ? "Reloading…" : "Tap to reload."}
      </span>
    </button>
  );
}
